import { IPharmacyInfo } from "@/Common/Api/Pharmacy/Dto/Responses/IPharmacyInfo";
import { PortalStatusButton } from "@/Common/Components/Portal/PortalStatusIcon";
import { JSXHelper } from "@/Common/Helpers/JSXHelper";
import { PortalHelper } from "@/Common/Helpers/PortalHelper";
import { Guid } from "@/Common/Models/Guid";
import { LabRequestFormMode, LabRequestType } from "@/Common/Models/Patient/LabRequest";
import { Patient } from "@/Common/Models/Patient/Patient";
import { EmployeeUIModel } from "@/Common/Models/UI/Employee/EmployeeUIModel";
import { ExportMode } from "@/ExportableFile/Models/enum/ExportMode";
import { SignedAuthorizationDocumentType } from "@/Patient/Models/MedicalNote/Enum/SignedAuthorizationDocumentType";
import { IPatientMedicalNoteModel } from "@/Patient/Models/UI/MedicalNote/IPatientMedicalNoteModel";
import { IPatientMedicalSummaryModel } from "@/Patient/Models/UI/MedicalSummary/IPatientMedicalSummaryModel";
import { ConsultationRequestsInboxItem, RequestsInboxItem } from "@/Portfolio/Models/MyNotesInboxItem";
import { PlanActionLinks } from "../PlanActionLinks";
import { CommunicationsSection } from "./CommunicationsSection";
import { ConsultationRequests } from "./ConsultationsRequests";
import { ExamRequests } from "./ExamRequests";
import { OtherPlans } from "./OtherPlans";
import { PrescriptionSection } from "./PrescriptionSection";
import { PrivateBillSection } from "./PrivateBillSection";

import styles from "./PlanSection.module.scss";

interface IPlanSectionProps {
    noteModel: IPatientMedicalNoteModel;
    summaryModel: IPatientMedicalSummaryModel;
    patientModel: Patient
    patientId: Guid;
    isEditMode: boolean;
    isReadOnly: boolean;
    preferredPharmacy: IPharmacyInfo;
    toggleSectionOpen: () => void;
    toggleAutoSave: () => void
    openConsultationRequestPopup: (consultationId: string) => void;
    openLabRequestPopup: (id: string, type: LabRequestType) => void;
    openSignExamRequestPopup: (model: RequestsInboxItem, exportMode: ExportMode) => void;
    openSignConsultationRequestPopup: (model: ConsultationRequestsInboxItem, exportMode: ExportMode) => void;
    onExamRequestPortalClick: (examRequest: RequestsInboxItem) => void;
    onConsultationRequestPortalClick: (consultationRequestsInboxItem: ConsultationRequestsInboxItem) => void;
    getRevokeSignaturePopupModel: (id: string, type: SignedAuthorizationDocumentType) => void;
    showNotePopup: (e: React.MouseEvent) => void;
    showClickableVisibility: (e: React.MouseEvent) => void;
    onAutosaveFieldBlur: () => void;
    onCloseClickableText: () => void;
    isSectionOpen: boolean;

}

function PlanSection(props: IPlanSectionProps): JSX.Element {
    const encounter = props.noteModel.get("Encounter");
    const isSignedOff = encounter.IsSignedOff;
    const notesPlan = props.noteModel.get("EncounterNotes").toArray().filter(x => x.Context == "OTH");
    const canQuickSignDocuments = cacheStore.ClinicSettings.EnableQuickDocumentSigning && !props.isReadOnly;
    const sentFaxes = props.noteModel?.get("SentFaxes") ?? {};

    const canSignFollowup = (model: ConsultationRequestsInboxItem | RequestsInboxItem, currentEmployee: EmployeeUIModel): boolean => {
        const lastModifiedEmployee = cacheStore.GetEmployeeByContactIdOrDefault(model.LastUpdateByContactId);
        const createdEmployee = cacheStore.GetEmployeeByContactIdOrDefault(model.CreatedByContactId);
        const isSignOnPrintAccessValid = cacheStore.ClinicSettings.EnableQuickDocumentSigning || sessionStore.signOnPrintAccess;

        return (
            isSignOnPrintAccessValid
            && (
                model.LastUpdateByContactId === currentEmployee.ContactId
                || model.RequestingStaffContactFacetId === currentEmployee.ContactFacetId
                || (
                    createdEmployee
                    && lastModifiedEmployee
                    && model.RequestingStaffContactFacetId === lastModifiedEmployee.ContactFacet_id
                    && createdEmployee.Contact_id === currentEmployee.ContactId
                )
            )
        );
    };

    const showRequestPopup = (
        requestItem: RequestsInboxItem,
        formMode: LabRequestFormMode = LabRequestFormMode.Creating,
        requestType: LabRequestType = LabRequestType.None
    ): void => {
        if (requestType == LabRequestType.RIS || requestType == LabRequestType.LIS) {
            const site = cacheStore.GetSiteById(encounter.SiteId);
            if (site.Model.InterfaceId != "UO_CMURANO") {
                sessionStore.showPopup("RIS is only available for notes associated to Murano.");
                return;
            }
        }

        props.openLabRequestPopup(formMode == LabRequestFormMode.Editable ? requestItem.RequestId : null, requestType);
    };

    const renderMedicalNoteRequest = (
        model: ConsultationRequestsInboxItem | RequestsInboxItem,
        currentEmployee: EmployeeUIModel
    ): JSX.Element => {
        const invalidCss = model.IsInvalid ? "invalid-medical-item" : "";

        let itemId: string;
        let title: string;
        let description: string;
        let descriptionClassName: string = "";
        let nameToDisplay: string;

        let onClick: () => void;
        let onPortalClick: () => void;
        let onRevokeSignatureClick: () => void;
        let onActionClick: (mode: ExportMode) => void;
        const getDescriptionsClassName = (): string => {
            if (props.isReadOnly) {
                return styles.inactiveTextColor;
            }
            else if (IsNullOrWhiteSpace(model.Reason)) {
                return styles.activeTextColor;
            }
            else {
                return styles.commonTextColor;
            }
        };

        if (model instanceof RequestsInboxItem) {
            const openSignPrintFaxExamRequestPopup = (exportMode: ExportMode): void => props.openSignExamRequestPopup(model, exportMode);

            itemId = model.RequestId;
            title = model.Reason ? `${model.Reason}\n${model.Request}` : model.Request;

            for (const sentFax of sentFaxes[itemId] ?? []) {
                sentFax.HasAttachment = true;
            }

            description = model.Request;
            descriptionClassName = getDescriptionsClassName();

            const status = cacheStore.GetLocalizedValueByLookupId(model.StatusLookupId);
            nameToDisplay = (cacheStore.ClinicSettings.EnableFollowUps && status)
                ? `[${status}] ${model.Reason || ""}`
                : `${model.Reason || ""}`;

            onClick = () => showRequestPopup(model, LabRequestFormMode.Editable);
            onActionClick = (mode: ExportMode) => openSignPrintFaxExamRequestPopup(mode);
            onPortalClick = () => props.onExamRequestPortalClick(model);
            onRevokeSignatureClick = () => props.getRevokeSignaturePopupModel(model.RequestId, SignedAuthorizationDocumentType.ExamRequest);
        }
        else if (model instanceof ConsultationRequestsInboxItem) {
            const openSignPrintFaxConsultationRequestPopup = (exportMode: ExportMode): void => props.openSignConsultationRequestPopup(model, exportMode);

            itemId = model.ConsultationRequestId;
            title = model.Speciality;

            for (const sentFax of sentFaxes[itemId] ?? []) {
                sentFax.HasAttachment = true;
            }

            description = cacheStore.ClinicSettings.EnableFollowUps
                ? `[${cacheStore.GetLocalizedValueByLookupId(model.StatusLookupId)}] ${model.Speciality}`
                : model.Speciality;

            onClick = () => props.openConsultationRequestPopup(model.ConsultationRequestId);
            onActionClick = (mode: ExportMode) => openSignPrintFaxConsultationRequestPopup(mode);
            onPortalClick = () => props.onConsultationRequestPortalClick(model);
            onRevokeSignatureClick = () => props.getRevokeSignaturePopupModel(model.ConsultationRequestId, SignedAuthorizationDocumentType.ConsultationRequest);
        }
        else {
            throw new Error("Medical note request inbox item must be a lab or consultation request.");
        }

        return (
            <div key={itemId} className={canQuickSignDocuments ? styles.requestSectionItemFormatted : styles.requestSectionItem}>
                <div className={`${styles.requestItemTitle} ${!props?.isReadOnly ? styles.planSectionContainer : ""}${canQuickSignDocuments ? styles.titleFormatted : ""}`}>
                    {!canQuickSignDocuments && (
                        <div className={styles.requestHeader}>•</div>
                    )}
                    <a onClick={onClick} title={title}>
                        <div className={`${invalidCss} ${(!!props?.isReadOnly || cacheStore.ClinicSettings.EnableQuickDocumentSigning) ? styles.requestTitleFormatted : styles.requestTitle}`}>
                            {nameToDisplay}
                        </div>
                        {cacheStore.ClinicSettings.EnableQuickDocumentSigning
                            ? (
                                <div className={`${descriptionClassName} ${invalidCss} ${styles.descriptionFormatted}`}>
                                    {description}
                                </div>
                            )
                            : (
                                <div className={`${descriptionClassName} ${invalidCss}`}>
                                    {JSXHelper.BreakStringIntoParagraphs(
                                        description,
                                        true,
                                        !props?.isReadOnly ?
                                            {
                                                textOverflow: "ellipsis",
                                                width: "375px",
                                                whiteSpace: "noWrap",
                                                overflow: "hidden"
                                            } : { width: "100%" }
                                    )}
                                </div>
                            )
                        }
                    </a>
                </div>
                <div className={styles.planActionLinks}>
                    {!model.IsInvalid && !props.isReadOnly && (
                        <PlanActionLinks
                            canSignElement={canSignFollowup(model, currentEmployee)}
                            onActionClick={onActionClick}
                            onRevokeSignature={onRevokeSignatureClick}
                            signedPrintAuthorizations={model.SignedPrintAuthorizations}
                            sentFaxInfo={sentFaxes[itemId]}
                            formId={itemId}
                            showPortalStatusButton={props.patientModel.IsRegisteredToPortal() || cacheStore.ClinicSettings.EnablePortalInvitationFlowV2}
                            portalStatusButton={(
                                <PortalStatusButton
                                    onClick={onPortalClick}
                                    {...PortalHelper.getPortalButtonPropsForShareableItem(model)}
                                />
                            )}
                        />
                    )}

                    {model instanceof RequestsInboxItem
                        && (model.Type === LabRequestType.RIS || model.Type === LabRequestType.LIS)
                        && model.SendStatus
                        && (
                            <div className={`padding-lg-right  ${styles.requestItems}`}>
                                <a>{` (${model.SendStatus})`}</a>
                            </div>
                        )
                    }
                    <div className={styles.requestClear} />
                </div>
            </div>
        );
    };

    const getExamRequestRenders = (): JSX.Element[] => {
        const currentEmployee = sessionStore.getCurrentEmployee();

        return props.noteModel
            .get("Requests")
            .map(model => renderMedicalNoteRequest(model, currentEmployee))
            .toArray();
    };

    const getConsultationRequestRenders = (): JSX.Element[] => {
        const currentEmployee = sessionStore.getCurrentEmployee();

        return props.noteModel
            .get("ConsultationRequests")
            .map(model => renderMedicalNoteRequest(model, currentEmployee))
            .toArray();
    };

    const requestRenders = getExamRequestRenders();
    const consultationRequestRenders = getConsultationRequestRenders();

    return (
        <div className="note-block medical-note-block">
            <div className="note-block-header" onClick={() => props.toggleSectionOpen()}>
                <div className="pull-right">
                    <span className={"glyphicon " + (props.isSectionOpen ? "glyphicon-chevron-up" : "glyphicon-chevron-down")} />
                </div>
                <h3>{cacheStore.GetString("PlanHeader")}</h3>
            </div>
            <div className={props.isReadOnly ? "no-hover" : ""}>
                {props.isSectionOpen && (
                    <div className="patient-note padding-none-bottom">
                        <table className="patient-note fullWidth">
                            <tbody>
                                {(!isSignedOff || (isSignedOff && !isArrayNullOrUndefinedOrEmpty(props.summaryModel?.get("Prescriptions")?.toArray()))) && (
                                    <PrescriptionSection
                                        noteModel={props.noteModel}
                                        encounter={encounter}
                                        prescriptions={props.summaryModel.get("Prescriptions")}
                                        isReadOnly={props.isReadOnly}
                                        isEditMode={props.isEditMode}
                                        isSignedOff={encounter.IsSignedOff}
                                        patientId={props.patientId}
                                        preferredPharmacy={props.preferredPharmacy}
                                        getRevokeSignaturePopupModel={(id: string, type: SignedAuthorizationDocumentType) =>
                                            props.getRevokeSignaturePopupModel(id, type)
                                        }
                                    />
                                )}
                                {(!isSignedOff || !isArrayNullOrUndefinedOrEmpty(requestRenders)) && (
                                    <ExamRequests
                                        isEditMode={props.isEditMode}
                                        isReadOnly={props.isReadOnly}
                                        requests={requestRenders}
                                        showRequestPopup={(request, mode, type) => showRequestPopup(request, mode, type)}
                                    />
                                )}
                                {(!isSignedOff || !isArrayNullOrUndefinedOrEmpty(consultationRequestRenders)) && (
                                    <ConsultationRequests
                                        isEditMode={props.isEditMode}
                                        isReadOnly={props.isReadOnly}
                                        consultations={consultationRequestRenders}
                                        openConsultationRequestPopup={id => props.openConsultationRequestPopup(id)}
                                    />
                                )}

                                {(!isSignedOff || (!IsNullOrWhiteSpace(encounter.OtherPlanNote) || (notesPlan.length > 0))) && (
                                    <OtherPlans
                                        isEditMode={props.isEditMode}
                                        isReadOnly={props.isReadOnly}
                                        isFollowUpTextVisible={props.noteModel.get("FollowUpPlanClickableTextVisible")}
                                        notesPlan={notesPlan}
                                        encounter={encounter}
                                        showNotePopup={e => props.showNotePopup(e)}
                                        onCloseClickableText={() => props.onCloseClickableText()}
                                        showClickableVisibility={e => props.showClickableVisibility(e)}
                                        onAutosaveFieldBlur={() => props.onAutosaveFieldBlur()}
                                        toggleAutoSave={() => props.toggleAutoSave()}
                                    />
                                )}
                                <CommunicationsSection
                                    model={props.noteModel}
                                    isReadOnly={props.isReadOnly}
                                    patientId={props.patientId}
                                    encounter={encounter}
                                />
                                <PrivateBillSection
                                    bills={props.noteModel.get("PrivateBills").toArray()}
                                    isEditMode={props.isEditMode}
                                    isReadOnly={props.isReadOnly}
                                    isSignedOff={isSignedOff}
                                />
                            </tbody>
                        </table>
                    </div>
                )}
            </div>
        </div>
    );
}

export { PlanSection };
